import type { ComponentMeta } from "@mijalowcode/types";
import { ComponentCategory, PropType } from "@mijalowcode/types";

export const ProTableMeta: ComponentMeta = {
  componentName: "ProTable",
  package: "antdPro",
  title: "高级表格",
  description: "ProTable 高级表格，支持更多功能",
  category: ComponentCategory.DATA_DISPLAY,
  visibleInComponentPanel: true,
  icon: "TableOutlined",
  canHaveChildren: false,
  aiUsage: {
    role: "data-table",
    configPaths: ["props.columns", "props.search", "props.searchConfig"],
    guidelines: [
      "数据列表优先使用 ProTable。",
      "ProTable 的搜索项来自 props.columns 的列配置，通过 dataIndex、valueType、valueEnum、fieldProps、search 等字段描述。",
      "需要隐藏某列搜索时，在对应 column 上配置 search: false。",
      "需要搜索但不展示为表格列时，在对应 column 上配置 hideInTable: true，并保留 dataIndex 作为请求参数名。",
    ],
    forbiddenPatterns: [
      "不要为了 ProTable 默认搜索区额外创建 SchemaForm。",
      "不要把与 ProTable columns 字段重复的搜索字段拆到独立 SchemaForm 中。",
    ],
    examples: [
      "用户列表的用户名、手机号、状态搜索应配置在 ProTable props.columns 中。",
      "状态搜索使用 column.valueType = 'select' 与 column.valueEnum 或 requestConfig。",
    ],
  },
  designToken: {
    themeComponentName: "Table",
    fields: [
      { name: "headerBg", label: "表头背景", type: "color" },
      { name: "headerColor", label: "表头文字", type: "color" },
      { name: "rowHoverBg", label: "行悬浮背景", type: "color" },
      { name: "borderColor", label: "边框颜色", type: "color" },
      {
        name: "cellPaddingBlock",
        label: "单元格纵向间距",
        type: "number",
        min: 0,
        max: 48,
      },
    ],
  },
  editorDnd: {
    dropMode: "slot",
    slotConfig: {
      areas: [
        { key: "toolbar", label: "工具栏" },
        {
          key: "search",
          label: "搜索区",
          disabled: true,
          disabledReason: "即将支持",
        },
        { key: "column", label: "某列插槽", requiresKey: true },
        {
          key: "action",
          label: "操作列",
          disabled: true,
          disabledReason: "即将支持",
        },
      ],
      defaultArea: "toolbar",
      keyOptionsSource: "protable.columns",
      keyPlaceholder: "请选择目标列",
    },
  },
  eventSources: [
    {
      name: "table.row.click",
      label: "行点击",
      trigger: "component",
    },
    {
      name: "table.pagination.change",
      label: "分页变化",
      trigger: "component",
    },
    {
      name: "table.request",
      label: "请求",
      trigger: "lifecycle",
    },
    {
      name: "table.request.start",
      label: "请求开始",
      trigger: "lifecycle",
    },
    {
      name: "table.request.success",
      label: "请求成功",
      trigger: "lifecycle",
    },
    {
      name: "table.request.error",
      label: "请求失败",
      trigger: "lifecycle",
    },
  ],
  capabilities: [
    {
      name: "reload",
      description: "刷新表格数据",
    },
    {
      name: "reset",
      description: "重置表格状态",
    },
  ],
  props: [
    // 列配置
    {
      name: "columns",
      label: "列配置",
      type: PropType.TABLE_COLUMNS,
      defaultValue: [
        {
          key: "col_1",
          title: "姓名",
          dataIndex: "name",
          valueType: "text",
        },
        {
          key: "col_2",
          title: "状态",
          dataIndex: "status",
          valueType: "select",
          valueEnum: {
            __bind: {
              source: "state",
              path: "tableStatusEnum",
              fallback: {
                open: { text: "未解决", status: "Error" },
                closed: { text: "已解决", status: "Success" },
              },
            },
          },
        },
        {
          key: "col_3",
          title: "地址",
          dataIndex: "address",
          valueType: "text",
          ellipsis: true,
        },
      ],
      description: "表格列配置",
      group: "列配置",
    },
    // 数据源配置
    {
      name: "__dataConfigSlot",
      label: "数据配置",
      type: PropType.OBJECT,
      defaultValue: {},
      description: "使用专用编辑器统一配置数据源绑定与格式化",
      group: "数据",
      customEditorSlot: "proTableDataConfig",
    },
    {
      name: "dataSource",
      label: "静态数据",
      type: PropType.JSON,
      defaultValue: [
        { key: "1", name: "张三", age: 32, address: "北京市朝阳区" },
        { key: "2", name: "李四", age: 28, address: "上海市浦东新区" },
        { key: "3", name: "王五", age: 35, address: "广州市天河区" },
      ],
      description: "关闭远程数据时使用的静态数据源",
      group: "数据",
      bindable: true,
      bindSources: ["state"],
    },
    {
      name: "rowKey",
      label: "行唯一键",
      type: PropType.STRING,
      defaultValue: "key",
      description: "表格行 key 的取值字段",
      group: "数据",
    },
    {
      name: "headerTitle",
      label: "表格标题",
      type: PropType.STRING,
      placeholder: "请输入表格标题",
      group: "基础",
    },
    {
      name: "size",
      label: "尺寸",
      type: PropType.ENUM,
      defaultValue: "middle",
      options: [
        { label: "大", value: "large" },
        { label: "中", value: "middle" },
        { label: "小", value: "small" },
      ],
      group: "基础",
    },
    {
      name: "bordered",
      label: "显示边框",
      type: PropType.BOOLEAN,
      defaultValue: false,
      group: "基础",
    },
    {
      name: "showHeader",
      label: "显示表头",
      type: PropType.BOOLEAN,
      defaultValue: true,
      group: "基础",
    },
    // 功能配置
    {
      name: "search",
      label: "显示搜索栏",
      type: PropType.BOOLEAN,
      defaultValue: true,
      description: "是否显示搜索表单",
      group: "搜索",
    },
    {
      name: "searchConfig.defaultCollapsed",
      label: "搜索默认折叠",
      type: PropType.BOOLEAN,
      defaultValue: true,
      description: "搜索表单首次渲染时是否默认折叠",
      group: "搜索",
      visibleWhen: { field: "search", equals: true },
    },
    {
      name: "searchConfig.resetText",
      label: "重置按钮文案",
      type: PropType.STRING,
      defaultValue: "重置",
      placeholder: "重置",
      group: "搜索",
      visibleWhen: { field: "search", equals: true },
    },
    {
      name: "searchConfig.searchText",
      label: "查询按钮文案",
      type: PropType.STRING,
      defaultValue: "查询",
      placeholder: "查询",
      group: "搜索",
      visibleWhen: { field: "search", equals: true },
    },
    {
      name: "searchInitialValuesSource",
      label: "搜索默认值来源",
      type: PropType.ENUM,
      defaultValue: "none",
      options: [
        { label: "不使用", value: "none" },
        { label: "Storage", value: "storage" },
      ],
      description: "进入页面时从 Storage 读取搜索表单默认值",
      group: "功能",
    },
    {
      name: "searchInitialValuesStorageType",
      label: "Storage 类型",
      type: PropType.ENUM,
      defaultValue: "sessionStorage",
      options: [
        { label: "sessionStorage", value: "sessionStorage" },
        { label: "localStorage", value: "localStorage" },
      ],
      group: "功能",
    },
    {
      name: "searchInitialValuesStorageKey",
      label: "Storage Key",
      type: PropType.STRING,
      placeholder: "如 customer-list-to-order-list",
      description: "读取跳转前写入的上下文数据",
      group: "功能",
    },
    {
      name: "__searchInitialValuesMappingsConfigSlot",
      label: "搜索字段映射",
      type: PropType.OBJECT,
      defaultValue: {},
      description: "使用交互式编辑器配置搜索字段映射",
      group: "功能",
      customEditorSlot: "proTableSearchMappingsConfig",
    },
    {
      name: "searchInitialValuesMappings",
      label: "搜索字段映射",
      type: PropType.JSON,
      defaultValue: [],
      description: '格式：[{"target":"customerId","from":"id"}]',
      group: "功能",
    },
    {
      name: "clearSearchInitialValuesAfterUse",
      label: "读取后清理",
      type: PropType.BOOLEAN,
      defaultValue: true,
      description:
        "读取 Storage 初始化搜索后删除该 key，避免普通进入页面仍带旧条件",
      group: "功能",
    },
    // {
    //   name: "rowSelectionEnabled",
    //   label: "行选择",
    //   type: PropType.BOOLEAN,
    //   defaultValue: false,
    //   description: "是否启用行选择",
    //   group: "功能",
    // },
    // {
    //   name: "rowSelectionType",
    //   label: "选择类型",
    //   type: PropType.ENUM,
    //   defaultValue: "checkbox",
    //   options: [
    //     { label: "多选", value: "checkbox" },
    //     { label: "单选", value: "radio" },
    //   ],
    //   group: "功能",
    // },
    // 分页配置
    {
      name: "paginationEnabled",
      label: "启用分页",
      type: PropType.BOOLEAN,
      defaultValue: true,
      group: "分页",
    },
    {
      name: "pageSize",
      label: "每页条数",
      type: PropType.NUMBER,
      defaultValue: 10,
      min: 1,
      max: 100,
      group: "分页",
    },
    {
      name: "showSizeChanger",
      label: "页码切换器",
      type: PropType.BOOLEAN,
      defaultValue: true,
      description: "是否显示每页条数切换器",
      group: "分页",
    },
    {
      name: "showQuickJumper",
      label: "快速跳转",
      type: PropType.BOOLEAN,
      defaultValue: false,
      description: "是否显示快速跳转",
      group: "分页",
    },
    // 滚动配置
    {
      name: "scrollX",
      label: "横向滚动宽度",
      type: PropType.NUMBER,
      placeholder: "不设置则自适应",
      description: "设置横向滚动的宽度",
      group: "滚动",
    },
    {
      name: "scrollY",
      label: "纵向滚动高度",
      type: PropType.NUMBER,
      placeholder: "不设置则不滚动",
      description: "设置纵向滚动的高度",
      group: "滚动",
    },
    {
      name: "autoHeight",
      label: "动态高度",
      type: PropType.BOOLEAN,
      defaultValue: false,
      description: "按 100vh 减去搜索区高度与偏移量自动计算纵向滚动高度",
      group: "滚动",
    },
    {
      name: "autoHeightOffset",
      label: "高度偏移",
      type: PropType.NUMBER,
      defaultValue: 0,
      min: 0,
      description: "动态高度额外减去的固定值",
      group: "滚动",
    },
    {
      name: "autoHeightMinY",
      label: "最小高度",
      type: PropType.NUMBER,
      defaultValue: 240,
      min: 0,
      description: "动态高度计算后的最小纵向滚动高度",
      group: "滚动",
    },
    // ============ 详情配置 ============
    // {
    //   name: "detailEnabled",
    //   label: "启用行详情",
    //   type: PropType.BOOLEAN,
    //   defaultValue: false,
    //   description: "是否支持展开查看详情",
    //   group: "详情",
    // },
    // {
    //   name: "detailType",
    //   label: "详情类型",
    //   type: PropType.ENUM,
    //   defaultValue: "expand",
    //   options: [
    //     { label: "展开行", value: "expand" },
    //     { label: "抽屉", value: "drawer" },
    //     { label: "弹窗", value: "modal" },
    //   ],
    //   group: "详情",
    // },
    // {
    //   name: "detailTitle",
    //   label: "详情标题",
    //   type: PropType.STRING,
    //   defaultValue: "详情",
    //   group: "详情",
    // },
  ],
  defaultProps: {
    columns: [
      { key: "col_1", title: "姓名", dataIndex: "name", valueType: "text" },
      {
        key: "col_2",
        title: "年龄",
        dataIndex: "age",
        valueType: "text",
        sorter: true,
      },
      {
        key: "col_3",
        title: "地址",
        dataIndex: "address",
        valueType: "text",
        ellipsis: true,
      },
    ],
    dataSource: [
      {
        name: "张三",
        age: 18,
        address: "北京市海淀区",
      },
    ],
    rowKey: "key",
    type: "",
    size: "middle",
    search: true,
    searchConfig: {
      defaultCollapsed: true,
      resetText: "重置",
      searchText: "查询",
    },
    searchInitialValuesSource: "none",
    searchInitialValuesStorageType: "sessionStorage",
    searchInitialValuesStorageKey: "",
    searchInitialValuesMappings: [],
    clearSearchInitialValuesAfterUse: true,
    options: false,
    paginationEnabled: true,
    pageSize: 10,
    showSizeChanger: true,
    autoHeight: false,
    autoHeightOffset: 0,
    autoHeightMinY: 240,
    apiId: "",
  },
  defaultStyle: {
    width: "100%",
    minHeight: 400,
  },
  preview: [
    {
      title: "基础表格",
      props: {
        headerTitle: "用户列表",
        search: false,
        columns: [
          { key: "col_1", title: "姓名", dataIndex: "name", valueType: "text" },
          { key: "col_2", title: "年龄", dataIndex: "age", valueType: "text" },
          {
            key: "col_3",
            title: "地址",
            dataIndex: "address",
            valueType: "text",
          },
        ],
        dataSource: [
          { key: "1", name: "张三", age: 18, address: "北京市海淀区" },
          { key: "2", name: "李四", age: 26, address: "上海市浦东新区" },
        ],
      },
    },
    {
      title: "带搜索",
      props: {
        headerTitle: "工单列表",
        search: true,
        columns: [
          {
            key: "col_1",
            title: "工单号",
            dataIndex: "ticketNo",
            valueType: "text",
          },
          {
            key: "col_2",
            title: "状态",
            dataIndex: "status",
            valueType: "select",
            valueEnum: {
              open: { text: "处理中", status: "Processing" },
              closed: { text: "已关闭", status: "Success" },
            },
          },
          {
            key: "col_3",
            title: "负责人",
            dataIndex: "owner",
            valueType: "text",
          },
        ],
        dataSource: [
          { key: "1", ticketNo: "TK-001", status: "open", owner: "张三" },
          { key: "2", ticketNo: "TK-002", status: "closed", owner: "李四" },
        ],
      },
    },
  ],
  docLinks: [
    {
      title: "ProTable 文档",
      url: "https://procomponents.ant.design/components/table?tab=api&current=1&pageSize=5",
      source: "antdPro",
    },
  ],
  npm: {
    package: "@ant-design/pro-components",
    exportName: "ProTable",
  },
  snippet: {
    componentName: "ProTable",
    package: "@ant-design/pro-components",
    props: {
      columns: [
        { key: "col_1", title: "姓名", dataIndex: "name", valueType: "text" },
        {
          key: "col_2",
          title: "年龄",
          dataIndex: "age",
          valueType: "text",
          sorter: true,
        },
        {
          key: "col_3",
          title: "地址",
          dataIndex: "address",
          valueType: "text",
          ellipsis: true,
        },
      ],
      dataSource: [
        {
          name: "张三",
          age: 18,
          address: "北京市海淀区",
        },
      ],
      type: "",
      rowKey: "key",
    },
  },
};
